import { useLoyaltyContext } from "../../../services/hooks/loyalty/useLoyaltyContext";
import { useIsLoyaltyOfferInstanceActive } from "../../../services/hooks/loyalty/useLoyaltyOfferInstanceActive";
import { useOpenGuestBuiltOffer } from "../../../services/hooks/loyalty/useOpenGuestBuiltOffer";
import { useMenuLookups } from "../../../services/hooks/useMenuLookups";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { removeLoyaltyOffer } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  getMenuSubItemsForOffer,
  useApplyLoyaltyItemOfferFlow,
} from "../../../services/redux/thunks/applyLoyaltyItemOfferFlow";
import { LocalizedString } from "../../../services/redux/types/CartLoyaltyV2Types";
import * as textPreparation from "../../../utils/help/textPreparation/textPreparation";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import { isGuestBuiltOfferRedeemed } from "../../../utils/loyalty/offerSelection";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { useOfferDescriptionHtml } from "../../../services/hooks/loyalty/useOfferDescriptionHtml";
import { sanitizeOfferHtml } from "../../../utils/loyalty/offerHtml";
import { LoyaltyOfferIcon } from "../LoyaltyOfferIcon/LoyaltyOfferIcon";
import {
  PromoBannerActionButton,
  PromoBannerCard,
} from "../PromoBanner/PromoBannerCard";

type LoyaltyFlashOfferBannerProps = {
  offer: LoyaltyOffer;
  /** Sizes the copy as the tiers design does, which sets the banner's height. */
  tierSized?: boolean;
  /**
   * The campaign's own words for this offer, when it authored any (story
   * #4087's server-fed rail). Absent everywhere else, which is why the offer's
   * `uiConfig` remains the default: this overrides only the copy, never the
   * offer's state, so apply/remove, expiry and availability still come from the
   * offer itself.
   */
  copy?: { title?: LocalizedString; description?: LocalizedString };
};

export default function LoyaltyFlashOfferBanner({
  offer,
  tierSized,
  copy,
}: LoyaltyFlashOfferBannerProps) {
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();
  const { timeZone, mappedOrderType, channel } = useLoyaltyContext();

  // ------- Selectors -------
  const selectedLanguage = useAppSelector(
    (state) => state.config.selectedLanguage
  );
  const isOfferInstanceActive = useIsLoyaltyOfferInstanceActive();
  const { subItemLookup } = useMenuLookups();
  const applyLoyaltyItemOfferFlow = useApplyLoyaltyItemOfferFlow();
  const openGuestBuiltOffer = useOpenGuestBuiltOffer();
  const partnershipCustomer = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data?.partnership
  );
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  // ------- Variables -------
  const applicabilityResult = offer.isOfferApplicable({
    timeZone,
    orderType: mappedOrderType ?? undefined,
    channel,
    entityImageLookup,
    itemStatusRedux,
    formatTime: (time) => textPreparation.formatTimeAMPMLocalized(t, time),
  });
  const hasDeletedMenuItems = offer.hasDeletedItems(entityImageLookup);
  const isDisabled = LoyaltyOffer.isLoyaltyOfferBlocked(
    false,
    applicabilityResult.applicable,
    hasDeletedMenuItems
  );
  const unavailableChipText = LoyaltyOffer.getLoyaltyOfferStatusChipText(
    isDisabled,
    applicabilityResult,
    t
  );

  const lang = selectedLanguage as keyof LocalizedString;
  const offerIdentifier = offer.shortId ?? offer.id;

  const isRedeemed = isOfferInstanceActive(offer);
  const description = useOfferDescriptionHtml(
    offer,
    Offer.getLocalizedValue(copy?.description) ?? offer.getDescription(lang)
  );
  /*
   * UX guide: the banner is "Icon + Title + description + expired date", and an
   * expiry only earns that line when it is near — 60 days.
   *
   * Both forms are gated by it. `uiConfig.validUntil` reads like merchant copy
   * but is not: loyalty *generates* it from `endDate`/`endTime` for every offer
   * that has one ("Valid until 10 Jan 2027"), and a merchant override is merged
   * onto that same field, so the client cannot tell the two apart. Ungating it
   * put a years-away date on every card.
   */
  const isExpiryWithin60Days = offer.isExpiryWithinDays(60);
  const validUntil = isExpiryWithin60Days
    ? offer.getValidUntil(lang)
    : undefined;
  const expiryDate = offer.getExpiryDate();
  const showGeneratedExpiry = !validUntil && expiryDate && isExpiryWithin60Days;

  // ------- Texts -------
  const remove = textPreparation.capitalizeFirstLetter(t("offers:remove"));
  const apply = textPreparation.capitalizeFirstLetter(t("offers:apply"));
  const expDate = t("offers:exp-date");

  // ------- Functions -------
  const createMarkup = (html: string) => ({ __html: sanitizeOfferHtml(html) });

  const handleApplyOffer = () => {
    const plain = offer.toPlain();

    // A BOGO or bundle cannot be applied from here — the guest has to choose
    // its items first, so the banner opens the offer page instead.
    if (isGuestBuiltOfferRedeemed(offer)) {
      openGuestBuiltOffer(offer);

      return;
    }

    applyLoyaltyItemOfferFlow({
      offer: plain,
      menuSubItems: getMenuSubItemsForOffer(plain, subItemLookup),
    });
  };

  const handleRemoveOffer = () => {
    dispatch(removeLoyaltyOffer());
  };

  return (
    <PromoBannerCard
      testId={`loyalty_offer_slider_${offerIdentifier}`}
      organization={Boolean(partnershipCustomer)}
      active={isRedeemed}
      unavailable={isDisabled}
      tierSized={tierSized}
      icon={
        /*
         * Primary, not the tier colour: a flash offer carries the restaurant's
         * identity (main, #227) — the tier tint belongs to tier surfaces.
         *
         * `iconOnly` keeps the offer motif badge here. Without it a BOGO or
         * bundle takes the badge over with the old circular collage treatment,
         * which the slider is explicitly not meant to show.
         */
        <LoyaltyOfferIcon
          offer={offer}
          sliderStyle
          iconOnly
          backgroundIconColor="primary"
        />
      }
      title={
        <span
          dangerouslySetInnerHTML={createMarkup(
            Offer.getLocalizedValue(copy?.title) ?? offer.getTitle(lang)
          )}
        />
      }
      clampDescription
      description={
        description ? (
          <span dangerouslySetInnerHTML={createMarkup(description)} />
        ) : undefined
      }
      chip={
        unavailableChipText
          ? {
              text: textPreparation.capitalizeFirstLetter(unavailableChipText),
              testId: "loyalty_slider-offer-unavailable",
            }
          : undefined
      }
      expiry={
        validUntil
          ? {
              value: validUntil,
              testId: `loyalty_${offerIdentifier}-validUntil`,
            }
          : showGeneratedExpiry
          ? {
              label: expDate,
              value: expiryDate as string,
              testId: `loyalty_${offerIdentifier}-expiryDate`,
            }
          : undefined
      }
      action={
        isRedeemed ? (
          <PromoBannerActionButton
            variant="remove"
            onClick={handleRemoveOffer}
            testId={`loyalty_remove_offer_${offerIdentifier}`}
          >
            {remove}
          </PromoBannerActionButton>
        ) : (
          !isDisabled && (
            <PromoBannerActionButton
              onClick={handleApplyOffer}
              testId={`loyalty_apply_offer_${offerIdentifier}`}
            >
              {apply}
            </PromoBannerActionButton>
          )
        )
      }
    />
  );
}
